/**
 * ============================================================================
 * 🚢 Marine Vessel Tactical Icon SVGs & Ship Type Color Mappings
 * ============================================================================
 */

export const VESSEL_COLORS = {
  cargo: '#38bdf8',      // Cyan / Cargo Container
  tanker: '#f59e0b',     // Amber / Tanker Oil/Gas
  passenger: '#10b981',  // Emerald / Ferry/Cruise
  tug: '#a855f7',        // Purple / Tug Towing
  fishing: '#06b6d4',    // Light Blue / Fishing
  military: '#ef4444',   // Red / Navy Military
  high_speed: '#ec4899', // Pink / Fast Craft
  sailing: '#14b8a6',    // Teal / Yacht
  general: '#94a3b8'     // Slate / Other
};

export const VESSEL_ICONS_SVG = {
  // Cargo Ship Silhouette (Top-down Hull with cargo containers)
  cargo: `
    <svg viewBox="0 0 36 36" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 2 C15 7, 13 13, 13 26 C13 31, 14 33, 18 33 C22 33, 23 31, 23 26 C23 13, 21 7, 18 2 Z" opacity="0.95"/>
      <rect x="15" y="10" width="6" height="3" fill="#ffffff" opacity="0.8" rx="0.5"/>
      <rect x="15" y="15" width="6" height="3" fill="#ffffff" opacity="0.8" rx="0.5"/>
      <rect x="15" y="20" width="6" height="3" fill="#ffffff" opacity="0.8" rx="0.5"/>
      <circle cx="18" cy="27" r="1.5" fill="#1e293b"/>
    </svg>
  `,

  // Tanker Ship (Rounded bow & long deck)
  tanker: `
    <svg viewBox="0 0 36 36" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 2 C14 8, 12 14, 12 27 C12 32, 14 34, 18 34 C22 34, 24 32, 24 27 C24 14, 22 8, 18 2 Z" opacity="0.95"/>
      <line x1="18" y1="8" x2="18" y2="24" stroke="#ffffff" stroke-width="1.5" opacity="0.7"/>
      <circle cx="18" cy="11" r="1.8" fill="#ffffff" opacity="0.8"/>
      <circle cx="18" cy="17" r="1.8" fill="#ffffff" opacity="0.8"/>
      <circle cx="18" cy="23" r="1.8" fill="#ffffff" opacity="0.8"/>
      <rect x="14.5" y="27" width="7" height="4" fill="#1e293b" rx="1"/>
    </svg>
  `,

  // Passenger / Cruise Ferry
  passenger: `
    <svg viewBox="0 0 36 36" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 2 C14.5 8, 13 14, 13 27 C13 32, 15 33, 18 33 C21 33, 23 32, 23 27 C23 14, 21.5 8, 18 2 Z" opacity="0.95"/>
      <rect x="14.5" y="8" width="7" height="18" fill="#ffffff" opacity="0.85" rx="1.5"/>
      <line x1="18" y1="10" x2="18" y2="24" stroke="#0f172a" stroke-width="1.2"/>
    </svg>
  `,

  // Tug Boat (Stout compact hull)
  tug: `
    <svg viewBox="0 0 36 36" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 5 C14 10, 13 15, 13 25 C13 29, 15 31, 18 31 C21 31, 23 29, 23 25 C23 15, 22 10, 18 5 Z" opacity="0.95"/>
      <rect x="15" y="14" width="6" height="7" fill="#ffffff" opacity="0.9" rx="1"/>
      <circle cx="18" cy="25" r="1.5" fill="#1e293b"/>
    </svg>
  `,

  // Fishing Boat (Medium hull with outriggers)
  fishing: `
    <svg viewBox="0 0 36 36" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 4 C15 9, 14 15, 14 26 C14 30, 15.5 32, 18 32 C20.5 32, 22 30, 22 26 C22 15, 21 9, 18 4 Z" opacity="0.95"/>
      <line x1="10" y1="18" x2="26" y2="18" stroke="#ffffff" stroke-width="1.2" opacity="0.8"/>
      <circle cx="18" cy="22" r="1.8" fill="#1e293b"/>
    </svg>
  `,

  // High Speed Craft
  high_speed: `
    <svg viewBox="0 0 36 36" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 2 L13 16 L14 29 L18 27 L22 29 L23 16 Z" opacity="0.95"/>
      <polygon points="18,6 15,17 21,17" fill="#ffffff" opacity="0.9"/>
    </svg>
  `,

  // Military / Patrol
  military: `
    <svg viewBox="0 0 36 36" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 1 L14 10 L13 28 L18 31 L23 28 L22 10 Z" opacity="0.95"/>
      <line x1="18" y1="4" x2="18" y2="20" stroke="#ffffff" stroke-width="1.5"/>
      <circle cx="18" cy="12" r="2" fill="#ef4444"/>
    </svg>
  `,

  // General Vessel Fallback
  general: `
    <svg viewBox="0 0 36 36" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 3 C15 8, 13.5 14, 13.5 26 C13.5 30, 15 32, 18 32 C21 32, 22.5 30, 22.5 26 C22.5 14, 21 8, 18 3 Z" opacity="0.95"/>
      <circle cx="18" cy="18" r="2.2" fill="#ffffff" opacity="0.85"/>
      <rect x="16" y="24" width="4" height="3" fill="#1e293b" rx="0.5"/>
    </svg>
  `
};

export function getVesselIconSvg(category = 'general') {
  const cat = (category || 'general').toLowerCase();
  return VESSEL_ICONS_SVG[cat] || VESSEL_ICONS_SVG.general;
}

export function getVesselColor(category = 'general') {
  const cat = (category || 'general').toLowerCase();
  return VESSEL_COLORS[cat] || VESSEL_COLORS.general;
}
